import { useState } from 'react';
import { FiChevronDown, FiChevronRight, FiMail, FiPhone, FiSmartphone } from 'react-icons/fi';
import type { Beneficiary } from '../../../../types/LegalCase';

interface BeneficiaryContactProps {
    beneficiary: Pick<Beneficiary, 'email' | 'phone' | 'mobile' | 'document'>;
    // Começa expandido? Padrão: recolhido (disclosure).
    defaultOpen?: boolean;
}

const hasAnyContact = (b: BeneficiaryContactProps['beneficiary']): boolean =>
    !!(b.email || b.phone || b.mobile || b.document);

// Bloco expansível de contato de um beneficiário (disclosure). Recolhido por padrão — um
// "Ver contato" com chevron que revela e-mail, telefone, celular e documento quando existem.
// Tailwind + dark mode, sem biblioteca. Usado no Resumo do caso (escritório responsável) e na
// página de Beneficiários. Não renderiza nada se o beneficiário não tem nenhum contato.
const BeneficiaryContact = ({ beneficiary, defaultOpen = false }: BeneficiaryContactProps) => {
    const [open, setOpen] = useState(defaultOpen);

    if (!hasAnyContact(beneficiary)) return null;

    return (
        <div className="space-y-1.5">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
                {open ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                {open ? 'Ocultar contato' : 'Ver contato'}
            </button>

            {open && (
                <div className="rounded-lg bg-gray-50 dark:bg-slate-900 p-2.5 space-y-1.5 text-sm">
                    {beneficiary.email && (
                        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                            <FiMail size={13} className="text-gray-400 dark:text-gray-500 shrink-0" />
                            <a href={`mailto:${beneficiary.email}`} className="text-blue-600 dark:text-blue-400 hover:underline break-all">
                                {beneficiary.email}
                            </a>
                        </div>
                    )}
                    {beneficiary.phone && (
                        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                            <FiPhone size={13} className="text-gray-400 dark:text-gray-500 shrink-0" />
                            <span>{beneficiary.phone}</span>
                        </div>
                    )}
                    {beneficiary.mobile && (
                        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                            <FiSmartphone size={13} className="text-gray-400 dark:text-gray-500 shrink-0" />
                            <span>{beneficiary.mobile}</span>
                        </div>
                    )}
                    {beneficiary.document && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            Documento: {beneficiary.document}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BeneficiaryContact;
